/* src/ui/windows/cartWindow.ts — the cart panel.
 *
 * Three tabs over one cart: what it is doing, what it could be, and who runs it. Everything on
 * screen is server-computed and rendered verbatim — the client never derives a currency value
 * (CLAUDE.md non-negotiable #2).
 *
 * Two deliberate interaction choices:
 *
 * **The price field is not live-bound.** A control that rewrites itself from a poll while the
 * player is dragging it is unusable — you aim for $9 and a poll snaps you back to $5. The
 * slider and the name field only adopt server values when they are not focused.
 *
 * **The gauges distinguish *ready* from *possible*.** `readyServings` is food in the window;
 * `servingsAvailable` is what the ingredients on board could become once someone preps. A cart
 * can be full of beef and selling nothing, and the panel has to make that legible rather than
 * showing one blended number.
 *
 * A freshly placed cart has no menu yet, and until it does the panel is the menu choice —
 * gauges reading zero and a price slider for a thing that does not exist would be noise.
 */

import { gameStore } from '../../core/store.ts';
import type { Cart, GameState } from '../../core/types.ts';
import {
  buyUpgrade,
  fetchRestockQuote,
  fetchUpgradeOptions,
  prepCart,
  renameCart,
  restockCart,
  setCartCuisine,
  setCartPrice,
  wipeCart,
  type UpgradeOption,
} from '../../net/gameApi.ts';
import { incomeInterpolator } from '../../sim/incomeInterpolator.ts';
import { el, text } from '../dom.ts';
import { formatMoney } from '../format.ts';
import { renderCuisineGrid } from '../panels/cuisinePicker.ts';
import { cleanGame, prepGame } from '../panels/miniGame.ts';
import { toast } from '../toast.ts';
import type { WindowManager } from './windowChrome.ts';

const WINDOW_ID = 'cart';
type Tab = 'info' | 'upgrades' | 'worker';

/** A donut gauge. `ratio` drives the arc; the label sits in the middle. */
function ring(value: Text, caption: string, tone: string): { node: HTMLElement; arc: HTMLElement } {
  const arc = el('div', { class: `gf-ring-arc gf-ring--${tone}` });
  const node = el(
    'div',
    { class: 'gf-ring' },
    arc,
    el('div', { class: 'gf-ring-mid' }, el('span', { class: 'gf-ring-value' }, value),
      el('span', { class: 'gf-ring-cap' }, caption)),
  );
  return { node, arc };
}

function setArc(arc: HTMLElement, ratio: number): void {
  const pct = Math.max(0, Math.min(1, ratio)) * 100;
  // A conic gradient is the cheapest possible donut: no SVG, no per-frame layout, and the
  // browser composites it. Only the custom property changes.
  arc.style.setProperty('--gf-arc', `${String(pct)}%`);
}

export function openCartWindow(windows: WindowManager): void {
  const existing = windows.get(WINDOW_ID);
  if (existing) {
    existing.focus();
    return;
  }

  let tab: Tab = 'info';
  let upgrades: readonly UpgradeOption[] = [];

  const win = windows.open({ id: WINDOW_ID, title: 'Cart', width: 430 });

  // ── header ──────────────────────────────────────────────────────────────────────────
  const breadcrumb = text('');
  const nameInput = el('input', {
    class: 'gf-cw-name',
    attrs: { type: 'text', maxlength: '40', 'aria-label': 'Cart name' },
  });
  const relocateBtn = el(
    'button',
    { class: 'gf-cw-relocate', attrs: { type: 'button' } },
    '✥ Relocate · $100',
  );

  // ── summary card ────────────────────────────────────────────────────────────────────
  const rateValue = text('$0');
  const priceLine = text('');
  const plLine = text('');
  const appealValue = text('0');
  const chips = el('div', { class: 'gf-cw-chips' });

  const slider = el('input', {
    class: 'gf-cw-slider',
    attrs: { type: 'range', min: '0', max: '100', step: '1', 'aria-label': 'Price' },
  });
  const sliderLo = text('$0');
  const sliderHi = text('$0');
  const optimalLabel = text('');
  const optimalMark = el('div', { class: 'gf-cw-optimal-mark' });

  // ── operations card ─────────────────────────────────────────────────────────────────
  const readyVal = text('0');
  const cleanVal = text('0%');
  const stockVal = text('0');
  const readyRing = ring(readyVal, 'READY', 'ready');
  const cleanRing = ring(cleanVal, 'CLEAN', 'clean');
  const stockRing = ring(stockVal, 'STOCK', 'stock');

  const prepHint = el('p', { class: 'gf-cw-hint' });
  const shortHint = el('p', { class: 'gf-cw-short' });
  const stockCap = text('');

  const prepBtn = el('button', { class: 'gf-cw-act', attrs: { type: 'button' } }, 'PREP');
  const wipeBtn = el('button', { class: 'gf-cw-act', attrs: { type: 'button' } }, 'WIPE DOWN');
  const restockBtn = el('button', { class: 'gf-cw-act', attrs: { type: 'button' } }, 'RESTOCK ×10');
  const restockHint = el('p', { class: 'gf-cw-hint' }, 'ingredients for 10 more');
  const marketBtn = el('button', { class: 'gf-cw-act', attrs: { type: 'button' } }, 'MARKET');

  const status = el('p', { class: 'gf-cw-status' });
  const tabBody = el('div', { class: 'gf-cw-tabbody' });

  const tabBar = el(
    'div',
    { class: 'gf-cw-tabs' },
    ...(
      [
        ['info', 'CART INFO'],
        ['upgrades', 'UPGRADES'],
        ['worker', 'WORKER'],
      ] as ReadonlyArray<readonly [Tab, string]>
    ).map(([id, label]) =>
      el(
        'button',
        {
          class: `gf-cw-tab${id === 'info' ? ' is-active' : ''}`,
          attrs: { type: 'button' },
          data: { tab: id },
          on: {
            click: () => {
              tab = id;
              for (const b of tabBar.querySelectorAll('.gf-cw-tab')) {
                b.classList.toggle('is-active', (b as HTMLElement).dataset['tab'] === id);
              }
              paint(gameStore.get());
              if (id === 'upgrades') void loadUpgrades();
            },
          },
        },
        label,
      ),
    ),
  );

  const infoPane = el(
    'div',
    { class: 'gf-cw-pane' },
    el(
      'div',
      { class: 'gf-cw-head' },
      el('div', null,
        el('div', { class: 'gf-cw-crumb' }, breadcrumb),
        nameInput),
      relocateBtn,
    ),
    el(
      'section',
      { class: 'gf-cw-card' },
      el(
        'div',
        { class: 'gf-cw-topline' },
        el('span', { class: 'gf-cw-rate' }, rateValue, el('em', null, '/min')),
        el('span', { class: 'gf-cw-price' }, priceLine),
      ),
      // Where that figure comes from. Without it the headline is an assertion; with it, cutting
      // the price below cost is visibly a loss rather than a mysteriously smaller number.
      el('div', { class: 'gf-cw-pl' }, plLine),
      el('div', { class: 'gf-cw-appeal' }, appealValue, el('span', null, ' ✦ Appeal')),
      chips,
      el('div', { class: 'gf-cw-sliderlabel' }, 'PRICE SET'),
      el(
        'div',
        { class: 'gf-cw-sliderwrap' },
        optimalMark,
        slider,
        el(
          'div',
          { class: 'gf-cw-sliderscale' },
          el('span', null, sliderLo),
          el('span', { class: 'gf-cw-optimal' }, optimalLabel),
          el('span', null, sliderHi),
        ),
      ),
    ),
    el(
      'section',
      { class: 'gf-cw-card' },
      el('div', { class: 'gf-cw-rings' }, readyRing.node, cleanRing.node, stockRing.node),
      el(
        'div',
        { class: 'gf-cw-actions' },
        el('div', null, prepBtn, prepHint),
        el('div', null, wipeBtn,
          el('p', { class: 'gf-cw-hint' }, 'Drifts down as you serve. Feeds appeal.')),
        el('div', null, restockBtn, restockHint, shortHint),
        el('div', null, marketBtn,
          el('p', { class: 'gf-cw-hint' }, stockCap)),
      ),
    ),
    status,
  );

  /*
   * The opening state of a just-placed cart: no menu, so nothing else in the panel means
   * anything yet. It replaces the tabs rather than sitting beside them — there is exactly one
   * thing to do here.
   */
  const menuStatus = el('p', { class: 'gf-cw-status' });
  const menuGrid = el('div', { class: 'gf-cw-menugrid' });
  const menuPane = el(
    'div',
    { class: 'gf-cw-pane gf-cw-menu' },
    el('h3', { class: 'gf-cw-menutitle' }, 'What do you sell?'),
    el(
      'p',
      { class: 'gf-cw-hint' },
      'Your pitch is set. Pick a menu and the cart opens with a day of stock on board.',
    ),
    menuGrid,
    menuStatus,
  );

  /*
   * Prep and wiping down are played here, over the panel rather than in a separate window: the
   * gauges they move are directly behind, so the result lands where you were already looking.
   */
  const mgHost = el('div', { class: 'gf-cw-mgwrap' });
  mgHost.hidden = true;

  win.body.replaceChildren(tabBar, menuPane, infoPane, tabBody, mgHost);

  // ── behaviour ───────────────────────────────────────────────────────────────────────

  /** Slider position ↔ price. Range is a band around the cuisine's optimal, not 0…∞. */
  const priceRange = (cart: Cart): readonly [number, number] => {
    const opt = cart.optimalPrice || 5;
    return [Math.max(0.5, opt * 0.35), opt * 2.6];
  };
  const posToPrice = (cart: Cart, pos: number): number => {
    const [lo, hi] = priceRange(cart);
    return lo + ((hi - lo) * pos) / 100;
  };
  const priceToPos = (cart: Cart, price: number): number => {
    const [lo, hi] = priceRange(cart);
    return Math.max(0, Math.min(100, ((price - lo) / (hi - lo)) * 100));
  };

  let pendingPrice: number | null = null;
  let commitTimer = 0;

  slider.addEventListener('input', () => {
    const cart = gameStore.get()?.cart;
    if (!cart) return;
    pendingPrice = posToPrice(cart, Number(slider.value));
    priceLine.textContent = `${formatMoney(pendingPrice)}/serving  (${formatMoney(
      pendingPrice - cart.unitCost,
    )} margin)`;
  });

  // Commit on release, not on every input event — dragging a slider would otherwise fire
  // dozens of writes, each of which re-runs the tick server-side.
  const commit = (): void => {
    const cart = gameStore.get()?.cart;
    if (!cart || pendingPrice === null) return;
    const next = pendingPrice;
    pendingPrice = null;
    window.clearTimeout(commitTimer);
    commitTimer = window.setTimeout(() => {
      status.textContent = 'Updating price…';
      void setCartPrice(cart.id, Number(next.toFixed(2)))
        .then(apply)
        .then(() => {
          status.textContent = `Price set to ${formatMoney(next)}.`;
        })
        .catch((err: unknown) => fail(err, 'Could not set the price.'));
    }, 60);
  };
  slider.addEventListener('change', commit);
  slider.addEventListener('pointerup', commit);

  nameInput.addEventListener('change', () => {
    const cart = gameStore.get()?.cart;
    if (!cart) return;
    const value = nameInput.value.trim();
    if (value === '' || value === cart.name) return;
    void renameCart(cart.id, value).then(apply).catch((err: unknown) => fail(err, 'Rename failed.'));
  });

  const apply = (state: GameState): void => {
    incomeInterpolator.sync(state);
    gameStore.set(state);
  };
  const fail = (err: unknown, fallback: string): void => {
    const msg = err instanceof Error ? err.message : fallback;
    status.textContent = msg;
    toast(msg);
  };

  /*
   * Run a skill interaction over the panel, then send its result. A cancelled game (Escape, or
   * the window closing) resolves to null and sends nothing — the server is only ever told about
   * a run the player actually finished.
   */
  interface MiniGame<T> {
    readonly el: HTMLElement;
    readonly done: Promise<T | null>;
    cancel: () => void;
  }
  let activeGame: { cancel: () => void } | null = null;

  function play<T>(
    game: MiniGame<T>,
    submit: (result: T) => Promise<GameState>,
    doing: string,
    failure: string,
  ): void {
    activeGame?.cancel();
    activeGame = game;
    mgHost.replaceChildren(game.el);
    mgHost.hidden = false;

    void game.done.then((result) => {
      mgHost.hidden = true;
      mgHost.replaceChildren();
      activeGame = null;
      if (result === null) return;
      status.textContent = doing;
      void submit(result)
        .then(apply)
        .catch((err: unknown) => fail(err, failure));
    });
  }

  prepBtn.addEventListener('click', () => {
    const cart = gameStore.get()?.cart;
    if (!cart) return;
    if (cart.servingsAvailable < 1) {
      status.textContent = 'No ingredients on board — buy some on the Market.';
      return;
    }
    // Cook up to a session's worth, bounded by what the ingredients and the counter allow —
    // the server clamps the same two limits, so this only keeps the game honest about how many
    // servings it is offering to make.
    const room = Math.min(cart.servingsAvailable, Math.max(0, cart.prepCap - cart.readyServings));
    play(
      prepGame(cart.cuisine, room),
      ({ quality, servings }) => {
        if (servings <= 0) return Promise.resolve(gameStore.get() as GameState);
        return prepCart(cart.id, quality, servings).then((state) => {
          const made = (state.cart?.readyServings ?? 0) - cart.readyServings;
          status.textContent = `Prepped ${made.toFixed(0)} of ${String(servings)} servings.`;
          return state;
        });
      },
      'Prepping…',
      'Could not prep.',
    );
  });

  wipeBtn.addEventListener('click', () => {
    const cart = gameStore.get()?.cart;
    if (!cart) return;
    play(
      cleanGame(),
      (q) =>
        wipeCart(cart.id, q).then((state) => {
          status.textContent = `Wiped down — ${(state.cart?.cleanliness ?? 0).toFixed(0)}% clean.`;
          return state;
        }),
      'Wiping down…',
      'Could not wipe down.',
    );
  });

  marketBtn.addEventListener('click', () => {
    (document.querySelector('[data-action="hud:market"]') as HTMLElement | null)?.click();
  });

  /*
   * One-click restock, because doing it by hand is a trap: the market's quantity box defaults to
   * 10, and "10 of everything" buys ten servings of the binding ingredient and fifty of the
   * garnish. The server buys exactly what ten more servings need, topping up rather than adding
   * blindly, and every line goes through the same market curve a manual order would.
   */
  const RESTOCK_SERVINGS = 10;
  restockBtn.addEventListener('click', () => {
    const cart = gameStore.get()?.cart;
    if (!cart || restockBtn.disabled) return;
    restockBtn.disabled = true;
    status.textContent = `Restocking ${String(RESTOCK_SERVINGS)} servings…`;
    void restockCart(cart.id, RESTOCK_SERVINGS)
      .then(apply)
      .then(() => {
        status.textContent = `Restocked for ${String(RESTOCK_SERVINGS)} more.`;
        quotedFor = '';
        void refreshQuote();
      })
      .catch((err: unknown) => fail(err, 'Could not restock.'))
      .finally(() => {
        restockBtn.disabled = false;
      });
  });

  /*
   * Price the restock before it is pressed. Keyed on the cart's stock so the quote is refreshed
   * when it actually changes rather than on every four-second poll — the answer is a round trip,
   * and the number only moves when the shopping list does.
   */
  let quotedFor = '';
  async function refreshQuote(): Promise<void> {
    const cart = gameStore.get()?.cart;
    if (!cart || cart.cuisine === null) return;
    const key = `${cart.id}:${JSON.stringify(cart.inventory)}`;
    if (key === quotedFor) return;
    quotedFor = key;
    try {
      const cost = await fetchRestockQuote(cart.id, RESTOCK_SERVINGS);
      restockHint.textContent = `${formatMoney(cost)} for 10 more servings`;
    } catch {
      restockHint.textContent = 'ingredients for 10 more';
    }
  }

  relocateBtn.addEventListener('click', () => {
    status.textContent = 'Relocating is coming with land ownership in the next phase.';
  });

  async function loadUpgrades(): Promise<void> {
    const cart = gameStore.get()?.cart;
    if (!cart) return;
    try {
      upgrades = await fetchUpgradeOptions(cart.id);
      paint(gameStore.get());
    } catch {
      /* The tab shows its empty state; the rest of the panel is unaffected. */
    }
  }

  function paintUpgrades(): void {
    if (upgrades.length === 0) {
      tabBody.replaceChildren(el('p', { class: 'gf-cw-hint' }, 'Loading upgrades…'));
      return;
    }
    const cart = gameStore.get()?.cart;
    tabBody.replaceChildren(
      ...upgrades.map((u) =>
        el(
          'div',
          { class: 'gf-up-row' },
          el(
            'div',
            { class: 'gf-up-id' },
            el('span', { class: 'gf-up-name' }, `${u.name} ${'★'.repeat(u.level)}`),
            el('span', { class: 'gf-up-desc' }, u.description),
          ),
          el('span', { class: 'gf-up-appeal' }, `+${String(u.appeal)} ✦`),
          u.level >= u.maxLevel
            ? el('span', { class: 'gf-up-max' }, 'MAX')
            : el(
                'button',
                {
                  class: 'gf-up-buy',
                  attrs: { type: 'button' },
                  on: {
                    click: () => {
                      if (!cart) return;
                      status.textContent = `Buying ${u.name}…`;
                      void buyUpgrade(cart.id, u.id)
                        .then(apply)
                        .then(loadUpgrades)
                        .catch((err: unknown) => fail(err, 'Could not buy that.'));
                    },
                  },
                },
                formatMoney(u.nextCost),
              ),
        ),
      ),
    );
  }

  /** True from the click until the server answers, so a poll cannot redraw the grid mid-request. */
  let choosing = false;
  /** How many cards are currently drawn; -1 before the first render. */
  let menuDrawn = -1;

  function paintMenu(state: GameState): void {
    const cart = state.cart;
    if (!cart || choosing) return;
    /*
     * The menu is static reference data, so it is drawn once. Redrawing it on every poll would
     * replace the buttons out from under the pointer, and a click whose mousedown and mouseup
     * straddle a redraw never fires at all — the picker would silently ignore every fourth
     * attempt.
     */
    if (menuDrawn === state.cuisines.length) return;
    menuDrawn = state.cuisines.length;
    menuGrid.replaceChildren(
      renderCuisineGrid(state.cuisines, (choice) => {
        choosing = true;
        menuStatus.textContent = `Opening as a ${choice.name.toLowerCase()} cart…`;
        void setCartCuisine(cart.id, choice.cuisine)
          .then(apply)
          .then(() => loadUpgrades())
          .catch((err: unknown) => {
            choosing = false;
            const msg = err instanceof Error ? err.message : 'Could not set that menu.';
            menuStatus.textContent = msg;
            toast(msg);
          });
      }),
    );
  }

  function paint(state: GameState | null): void {
    const cart = state?.cart ?? null;

    // A cart on the map with no menu yet: that choice is the entire panel until it is made.
    const needsMenu = state !== null && cart !== null && cart.placed && cart.cuisine === null;
    menuPane.hidden = !needsMenu;
    tabBar.hidden = needsMenu;
    infoPane.hidden = needsMenu || tab !== 'info';
    tabBody.hidden = needsMenu || tab === 'info';
    if (needsMenu) {
      paintMenu(state);
      return;
    }

    if (tab === 'upgrades') paintUpgrades();
    if (tab === 'worker') {
      tabBody.replaceChildren(
        el('p', { class: 'gf-cw-hint' },
          'Hiring staff arrives with the labour market. A worker will run the cart while you are away.'),
      );
    }
    if (!cart || !state || !cart.placed) return;

    breadcrumb.textContent = `${(cart.cuisine ?? 'food').toUpperCase()} CART · MY CITY`;
    if (document.activeElement !== nameInput) nameInput.value = cart.name;

    /*
     * The headline is **profit**, not takings. Demand is elastic, so takings rise as the price
     * falls — halving the price nearly doubles the revenue rate while each serving sells below
     * what it cost to make. A rate that went up as the decision got worse would be actively
     * misleading, so the number shown is what the cart actually earns, and the line under it
     * shows the subtraction.
     */
    const income = state.income;
    rateValue.textContent = formatMoney(income.netPerMin).replace(/\.00$/, '');
    rateValue.parentElement?.classList.toggle('is-loss', income.netPerMin < 0);
    plLine.textContent =
      income.grossPerMin === 0 && income.netPerMin === 0
        ? 'Nothing prepped — the cart is not serving anyone.'
        : `${formatMoney(income.grossPerMin)} takings − ${formatMoney(
            income.ingredientCostPerMin,
          )} ingredients, per minute`;
    if (document.activeElement !== slider && pendingPrice === null) {
      priceLine.textContent = `${formatMoney(cart.pricePerDog)}/serving  (${formatMoney(
        cart.unitCost,
      )} to make)`;
      slider.value = String(priceToPos(cart, cart.pricePerDog));
    }

    const [lo, hi] = priceRange(cart);
    sliderLo.textContent = formatMoney(lo);
    sliderHi.textContent = formatMoney(hi);
    optimalLabel.textContent = `OPTIMAL ${formatMoney(cart.optimalPrice)}`;
    optimalMark.style.left = `${String(priceToPos(cart, cart.optimalPrice))}%`;

    const a = cart.appealBreakdown;
    appealValue.textContent = a.total.toFixed(0);
    chips.replaceChildren(
      ...(
        [
          ['Design', a.design],
          ['Cleanliness', a.cleanliness],
          ['Menu', a.menu],
          ['Tune-Up', a.tuneup],
        ] as ReadonlyArray<readonly [string, number]>
      ).map(([label, v]) =>
        el('span', { class: 'gf-cw-chip' }, `${label}: `, el('b', null, `+${v.toFixed(0)}`)),
      ),
    );

    // Ready food vs ingredients-on-board are different numbers and must read as different.
    readyVal.textContent = cart.readyServings.toFixed(0);
    setArc(readyRing.arc, cart.prepCap > 0 ? cart.readyServings / cart.prepCap : 0);
    cleanVal.textContent = `${cart.cleanliness.toFixed(0)}%`;
    setArc(cleanRing.arc, cart.cleanliness / 100);
    stockVal.textContent = cart.servingsAvailable.toFixed(0);
    setArc(stockRing.arc, cart.prepCap > 0 ? cart.servingsAvailable / cart.prepCap : 0);
    stockCap.textContent = `servings of stock · ${cart.capacity.toFixed(0)}/min counter`;

    const per = cart.recipe.map((r) => `${String(r.qty)} ${r.name.toLowerCase()}`).join(' + ');
    prepHint.textContent = per === '' ? '' : `${per} each`;

    const short = cart.recipe.filter((r) => r.have < r.qty).map((r) => r.name);
    shortHint.textContent = short.length > 0 ? `Out of ${short.join(', ')}` : '';
    shortHint.hidden = short.length === 0;

    readyRing.node.classList.toggle('is-empty', cart.readyServings < 0.5);
    prepBtn.textContent = cart.servingsAvailable < 1 ? 'NO INGREDIENTS' : 'PREP';
    prepBtn.classList.toggle('is-primary', cart.readyServings < 0.5 && cart.servingsAvailable >= 1);
    // When there is nothing left to cook, restocking is the move — say so with the highlight.
    restockBtn.classList.toggle('is-primary', cart.servingsAvailable < 1);
    void refreshQuote();
  }

  const unsubscribe = gameStore.subscribe(paint);
  paint(gameStore.get());
  void loadUpgrades();

  const close = win.close.bind(win);
  win.close = (): void => {
    unsubscribe();
    activeGame?.cancel();
    window.clearTimeout(commitTimer);
    close();
  };
}
